// Doom-style first-person pump shotgun, seen from behind in perspective:
// pointed muzzle + twin barrels with the iconic bright reflections up top, a
// dark receiver body widening toward the viewer, and two tan hands gripping the
// lower corners. Baked once at lo-res then upscaled x2 nearest, matching the
// chunky pixel look of the background. Transparent outside the weapon.

const LW = 180;
const LH = 130;
const PIXEL = 2;

// gun metal
const DK = "#1b1d24";
const BODY = "#26282f";
const MID = "#4a4f5a";
const LT = "#9aa0ad";
const HI = "#d7dbe2";
// skin (hands)
const SK_L = "#cf9258";
const SK_M = "#a96b3c";
const SK_D = "#7a4a25";

function roundRect(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

// x-bounds of the barrel trapezoid at a given y (perspective).
function barrelBounds(y: number): [number, number] {
  const t = (y - 16) / (50 - 16);
  return [76 + (62 - 76) * t, 104 + (118 - 104) * t];
}

function paintBarrels(c: CanvasRenderingContext2D) {
  // muzzle tip
  c.fillStyle = DK;
  c.beginPath();
  c.moveTo(90, 2);
  c.lineTo(80, 16);
  c.lineTo(100, 16);
  c.closePath();
  c.fill();

  // barrel block (perspective trapezoid: narrow far end -> wide near)
  c.fillStyle = BODY;
  c.beginPath();
  c.moveTo(76, 16);
  c.lineTo(104, 16);
  c.lineTo(118, 50);
  c.lineTo(62, 50);
  c.closePath();
  c.fill();

  // ribbed vents across the barrel (horizontal machined segments)
  for (let y = 19; y < 50; y += 4) {
    const [xl, xr] = barrelBounds(y);
    c.fillStyle = HI;
    c.fillRect(Math.round(xl), y, Math.round(xr - xl), 1); // bright rib top
    c.fillStyle = DK;
    c.fillRect(Math.round(xl), y + 2, Math.round(xr - xl), 1); // shadow groove
  }

  // raised center sight rib with notches
  for (let y = 16; y < 50; y += 1) {
    const w = 6;
    c.fillStyle = y % 4 === 0 ? DK : MID;
    c.fillRect(90 - w / 2, y, w, 1);
  }
  c.fillStyle = HI;
  c.fillRect(89, 16, 2, 34); // rib top highlight
}

function paintBody(c: CanvasRenderingContext2D) {
  // receiver body (widening toward the viewer)
  c.fillStyle = BODY;
  c.beginPath();
  c.moveTo(62, 50);
  c.lineTo(118, 50);
  c.lineTo(134, 104);
  c.lineTo(46, 104);
  c.closePath();
  c.fill();

  // top rim highlight
  c.fillStyle = MID;
  c.fillRect(62, 50, 56, 2);

  // shade the right half for a bit of form
  c.fillStyle = "rgba(0,0,0,0.18)";
  c.beginPath();
  c.moveTo(90, 50);
  c.lineTo(118, 50);
  c.lineTo(134, 104);
  c.lineTo(90, 104);
  c.closePath();
  c.fill();

  // center seam + worn streaks
  c.fillStyle = DK;
  c.fillRect(89, 50, 2, 54);
  c.fillStyle = "rgba(80,50,30,0.25)";
  c.fillRect(70, 60, 3, 40);
  c.fillRect(108, 64, 3, 34);
}

function paintHand(c: CanvasRenderingContext2D, side: -1 | 1) {
  c.save();

  // forearm / palm mass in the bottom corner
  c.fillStyle = SK_M;
  const hx = side === 1 ? 94 : 38;
  roundRect(c, hx, 84, 48, 50, 12);
  c.fill();

  // knuckle highlight
  c.fillStyle = SK_L;
  roundRect(c, hx + 4, 88, 40, 14, 8);
  c.fill();

  // four fingers wrapping around the OUTER side
  for (let i = 0; i < 4; i++) {
    const fy = 82 + i * 10;
    const fx = side === 1 ? 118 : 40;
    c.fillStyle = SK_L;
    roundRect(c, fx, fy, 22, 8, 4);
    c.fill();
    c.fillStyle = SK_D;
    c.fillRect(fx, fy + 7, 22, 1);
  }

  // thumb toward the CENTER (the two thumbs meet in the middle)
  c.fillStyle = SK_M;
  roundRect(c, side === 1 ? 72 : 90, 78, 18, 16, 7);
  c.fill();

  c.restore();
}

// Deterministic grain in [0,1) for subtle texture.
function hash2(x: number, y: number): number {
  let n = (x * 374761393 + y * 668265263) | 0;
  n = (n ^ (n >> 13)) * 1274126177;
  return ((n ^ (n >> 16)) >>> 0) / 4294967295;
}

function applyGrain(c: CanvasRenderingContext2D) {
  const img = c.getImageData(0, 0, LW, LH);
  const d = img.data;
  for (let y = 0; y < LH; y++) {
    for (let x = 0; x < LW; x++) {
      const i = (y * LW + x) * 4;
      if (d[i + 3] === 0) continue; // skip transparent
      const delta = (hash2(x, y) - 0.5) * 16;
      d[i] = Math.max(0, Math.min(255, d[i] + delta));
      d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + delta));
      d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + delta));
    }
  }
  c.putImageData(img, 0, 0);
}

export const GUN_SPRITE_W = LW * PIXEL;
export const GUN_SPRITE_H = LH * PIXEL;

export function paintGunDataURL(): string {
  const lo = document.createElement("canvas");
  lo.width = LW;
  lo.height = LH;
  const c = lo.getContext("2d");
  if (!c) throw new Error("2d context unavailable");
  c.imageSmoothingEnabled = false;

  paintBarrels(c);
  paintBody(c);
  paintHand(c, -1);
  paintHand(c, 1);
  applyGrain(c);

  const out = document.createElement("canvas");
  out.width = GUN_SPRITE_W;
  out.height = GUN_SPRITE_H;
  const o = out.getContext("2d");
  if (!o) throw new Error("2d context unavailable");
  o.imageSmoothingEnabled = false;
  o.drawImage(lo, 0, 0, LW, LH, 0, 0, GUN_SPRITE_W, GUN_SPRITE_H);
  return out.toDataURL();
}
